import api from './api';
import type {
  ProducerOption,
  RegionOption,
  GrapeOption,
  WineColor,
  WineType,
  WineSubType,
} from './wineNameService';
import type { Wine } from './wineService';

/**
 * venueWineRequestService — venue-scoped, read-only pickers + a single
 * provisional-create endpoint for VENUE_USER (and admins working in venue
 * context).
 *
 * Picker searches: producer / region / grape / wine-name. All are read-only
 * lookups — venue users cannot mutate any of these tables. The request
 * endpoint POSTs the form payload to the backend, which forces
 * needsReview=true, source='venue_request', createdBy=req.user.id.
 *
 * Used by RequestWineModal. The existing admin CreateWineModal still uses
 * wineNameService / wineService directly and is unchanged.
 */

const base = (venueId: string) => `/admin/venue/${venueId}/wine-lookup`;

export interface WineNameLookupOption {
  _id: string;
  displayName: string;
  color?: WineColor;
}

export interface RequestNewWinePayload {
  // Either provide an existing wineNameId…
  wineNameId?: string;
  // …or the WineName fields needed to create one alongside the Wine.
  producerId?: string;
  regionId?: string;
  labelGrapeIds?: string[];
  specialName?: string;
  color?: WineColor;
  type?: WineType;
  subType?: WineSubType;
  // Always required:
  vintageType: 'single_vintage' | 'non_vintage' | 'multi_vintage';
  vintage?: number;
  baseVintage?: number;
}

const venueWineRequestService = {
  searchProducers: async (venueId: string, q: string): Promise<ProducerOption[]> => {
    const response = await api.get(`${base(venueId)}/producers/search`, { params: { q } });
    const data = response.data?.data ?? [];
    return data.map((p: any) => ({ _id: p._id, name: p.name, fullName: p.fullName }));
  },

  searchRegions: async (venueId: string, q: string): Promise<RegionOption[]> => {
    const response = await api.get(`${base(venueId)}/regions/search`, { params: { q } });
    const data = response.data?.data ?? [];
    return data.map((r: any) => ({
      _id: r._id,
      name: r.name,
      hierarchyNames: r.hierarchyNames,
    }));
  },

  searchGrapes: async (venueId: string, q: string): Promise<GrapeOption[]> => {
    const response = await api.get(`${base(venueId)}/grapes/search`, { params: { q } });
    const data = response.data?.data ?? [];
    return data.map((g: any) => ({ _id: g._id, primaryName: g.primaryName, color: g.color }));
  },

  searchWineNames: async (venueId: string, q: string): Promise<WineNameLookupOption[]> => {
    const response = await api.get(`${base(venueId)}/wine-names/search`, { params: { q } });
    const data = response.data?.data ?? [];
    return data.map((wn: any) => ({ _id: wn._id, displayName: wn.displayName, color: wn.color }));
  },

  /**
   * Submit a provisional wine. Backend forces needsReview, source, createdBy
   * server-side — anything sent in those fields by the client is ignored.
   * Response is the populated Wine record.
   */
  requestNew: async (venueId: string, payload: RequestNewWinePayload): Promise<Wine> => {
    const response = await api.post(`${base(venueId)}/request`, payload);
    return response.data;
  },

  /**
   * Request a provisional Producer. Backend forces needsReview / source /
   * createdBy. Response is shaped as `ProducerOption` so it drops directly
   * into the modal's picker. On 409 the API includes `existing` — caller
   * can surface it as "select the existing record instead" if desired.
   */
  requestProducer: async (
    venueId: string,
    payload: { name: string; fullName?: string; country?: string },
  ): Promise<ProducerOption> => {
    const response = await api.post(`${base(venueId)}/producers/request`, payload);
    const p = response.data;
    return { _id: p._id, name: p.name, fullName: p.fullName, needsReview: !!p.needsReview };
  },

  /**
   * Request a provisional WineRegion. `parentRegionId` optional — omit for
   * a top-level region (mirrors the existing admin createRegionOnFly).
   */
  requestRegion: async (
    venueId: string,
    payload: { name: string; localName?: string; parentRegionId?: string },
  ): Promise<RegionOption> => {
    const response = await api.post(`${base(venueId)}/regions/request`, payload);
    const r = response.data;
    return { _id: r._id, name: r.name, hierarchyNames: r.hierarchyNames, needsReview: !!r.needsReview };
  },

  /**
   * Request a provisional Grape. Backend defaults `color` to RED if omitted.
   */
  requestGrape: async (
    venueId: string,
    payload: { primaryName: string; color?: 'RED' | 'WHITE' | 'PINK_ROSE' },
  ): Promise<GrapeOption> => {
    const response = await api.post(`${base(venueId)}/grapes/request`, payload);
    const g = response.data;
    return { _id: g._id, primaryName: g.primaryName, color: g.color, needsReview: !!g.needsReview };
  },
};

export default venueWineRequestService;
